import dayjs, { Dayjs } from "dayjs";
import { BillingSettings } from "../model/client/ClientSettings";
import { PaymentFrequency } from "../model/members/MembershipCategoryRow";

// Frontend mirror of the backend BillingCycleService (apps/api .../Billing).
// Keep the two in lockstep: the pro-rata amount shown/charged here must match
// the valid_to the API computes for the same join date and client config.
//
//   cycleDay == null  → last day of the month (legacy default)
//   cycleDay == 1..28 → that day of the month
//   cutoffDay         → join-day threshold; joining on/after it adds a full cycle

export const DEFAULT_PRO_RATA_CUTOFF_DAY = 23;

export interface ProRata {
    validTo: Dayjs;
    amount: number;
    rolledForward: boolean;
}

function cycleDayOf(billing?: BillingSettings): number | null {
    // Treat a missing/invalid value as "last day of month" so behaviour matches
    // the backend default rather than guessing a fixed day.
    return billing?.monthlyCycleDay ?? null;
}

function cutoffDayOf(billing?: BillingSettings): number {
    return billing?.proRataCutoffDay ?? DEFAULT_PRO_RATA_CUTOFF_DAY;
}

// The anchor (cycle-end) that lives in the same month as `date`.
function anchorInMonth(date: Dayjs, cycleDay: number | null): Dayjs {
    const startOfDay = date.startOf("day");
    if (cycleDay == null) {
        return startOfDay.endOf("month").startOf("day");
    }
    const day = Math.min(cycleDay, startOfDay.daysInMonth());
    return startOfDay.date(day);
}

// The collection date a run will actually use. Mirrors the backend
// BillingCycleService.AlignCollectionDate: the anchor in the SAME month as the
// requested date, which may fall before it (pick the 10th on a 7th-of-month cycle
// and the run collects on the 7th). The API snaps the date this way whatever the
// operator picked, so every amount shown or sent has to be computed for the snapped
// date — an item's validity window is judged against the date money moves.
export function alignCollectionDate(requested: Dayjs, billing?: BillingSettings): Dayjs {
    return anchorInMonth(requested, cycleDayOf(billing));
}

export function nextAnchorOnOrAfter(date: Dayjs, billing?: BillingSettings): Dayjs {
    const cycleDay = cycleDayOf(billing);
    const anchor = anchorInMonth(date, cycleDay);
    return anchor.isBefore(date.startOf("day"))
        ? anchorInMonth(date.add(1, "month"), cycleDay)
        : anchor;
}

// Advance one whole cycle from the cycle `fromDate` falls in.
export function nextCycleEnd(fromDate: Dayjs, billing?: BillingSettings): Dayjs {
    return anchorInMonth(fromDate.add(1, "month"), cycleDayOf(billing));
}

// A Membership promotion's valid-to: `durationMonths` from `validFrom`, optionally snapped forward
// to the client's billing anchor. Mirrors the backend MembershipPromotionHandler.Plan (FORT-314/319):
// snap on → NextAnchorOnOrAfter(validFrom + durationMonths, cycleDay); snap off → exactly
// validFrom + durationMonths.
//
// This is a DISPLAY DEFAULT only — it fills the editable "Valid to" field live as the operator
// changes "Valid from", without a round-trip per date pick. The value that actually gets committed
// comes back from the apply-promotion preview (FORT-318), which recomputes it server-side; the
// modal writes that answer back into the field before showing the confirmation panel.
export function promotionValidTo(
    validFrom: Dayjs,
    durationMonths: number,
    snapToBillingCycle: boolean,
    billing?: BillingSettings
): Dayjs {
    const termEnd = validFrom.add(durationMonths, "month");
    return snapToBillingCycle ? nextAnchorOnOrAfter(termEnd, billing) : termEnd;
}

// The extra period a billing-cycle snap adds after a fixed-term promotion ends (FORT-319), as a
// fraction of the cycle it lands in — mirrors BillingCycleService.ProRataTail. Zero when `termEnd`
// is already on an anchor (the term ends exactly on a billing date, so nothing is snapped on).
export function promotionProRataTailFraction(termEnd: Dayjs, billing?: BillingSettings): number {
    const cycleDay = cycleDayOf(billing);
    const start = termEnd.startOf("day");
    const validTo = nextAnchorOnOrAfter(start, billing);
    const previousAnchor = anchorInMonth(validTo.subtract(1, "month"), cycleDay);

    const cycleLength = validTo.diff(previousAnchor, "day");
    const tailDays = validTo.diff(start, "day");
    return cycleLength > 0 ? tailDays / cycleLength : 0;
}

// A Membership promotion's fee: the fixed fee, plus — only when snapping AND pro-rata are on — the
// snapped extra period billed at the member category's normal price (FORT-319). Mirrors
// MembershipPromotionHandler.Plan, including its Monthly-only guard: the tail is a slice of one
// monthly cycle, so it's only charged against a Monthly category price (a non-Monthly category's
// tail is free), and the tail is rounded before adding the fixed fee to match the server penny-for-
// penny. DISPLAY DEFAULT only; the apply-promotion preview is authoritative.
export function promotionFee(
    fixedFee: number,
    categoryPrice: number,
    categoryPaymentFrequency: PaymentFrequency,
    validFrom: Dayjs,
    durationMonths: number,
    snapToBillingCycle: boolean,
    proRata: boolean,
    billing?: BillingSettings
): number {
    if (!snapToBillingCycle || !proRata || categoryPaymentFrequency !== "Monthly") return fixedFee;
    const fraction = promotionProRataTailFraction(validFrom.add(durationMonths, "month"), billing);
    const tail = parseFloat((categoryPrice * fraction).toFixed(2));
    return parseFloat((fixedFee + tail).toFixed(2));
}

// Pro-rate a new member's first monthly period from their join date.
export function calculateProRata(
    price: number,
    joinDate: Dayjs,
    billing?: BillingSettings
): ProRata {
    const cycleDay = cycleDayOf(billing);
    const cutoffDay = cutoffDayOf(billing);
    const start = joinDate.startOf("day");

    const firstAnchor = nextAnchorOnOrAfter(start, billing);
    const previousAnchor = anchorInMonth(firstAnchor.subtract(1, "month"), cycleDay);

    const cycleLength = firstAnchor.diff(previousAnchor, "day");
    const daysCharged = firstAnchor.diff(start, "day") + 1;
    const baseFraction = cycleLength > 0 ? daysCharged / cycleLength : 0;

    const rolledForward = start.date() >= cutoffDay;
    const fraction = rolledForward ? baseFraction + 1 : baseFraction;
    const validTo = rolledForward ? nextCycleEnd(firstAnchor, billing) : firstAnchor;

    return {
        validTo,
        amount: parseFloat((price * fraction).toFixed(2)),
        rolledForward,
    };
}

// Convenience for callers that only need the first-period end date (e.g. the
// activate-member default), with the legacy annual rule (one year out) kept.
export function proRataValidTo(
    joinDate: Dayjs,
    billing: BillingSettings | undefined,
    paymentFrequency: string
): Dayjs {
    if (paymentFrequency === "Annual") {
        return joinDate.add(1, "year");
    }
    return calculateProRata(0, joinDate, billing).validTo;
}

// Default collection date for a new direct debit run: the next cycle day on or
// after today (mirrors the backend AlignCollectionDate within the chosen month).
export function nextCollectionDate(billing?: BillingSettings, from: Dayjs = dayjs()): Dayjs {
    return nextAnchorOnOrAfter(from, billing);
}
